<template>
  <div>
    <a-button type="primary" @click="openAddDialog" style="margin-bottom: 16px">
      添加广告配置
    </a-button>
    <x-table
      :columns="columns"
      :data-source="tableData"
      :loading="loading"
      rowKey="name"
      bordered
      :pagination="false"
      :isEexpandedRowRender="true"
      @expand="onExpand"
    >
      <template #count="{ record }">
        <a @click="viewChannels(record)">{{ record.count }}</a>
      </template>

      <template #expandedRowRender="{ record }">
        <div v-if="record.detail && record.detail.length > 0">
          <!-- 批量删除按钮 -->
          <div v-if="(rowSelectionMap[record.name]?.selectedRowKeys?.length || 0) > 0" style="margin-bottom: 12px; text-align: right;">
            <a-button 
              type="primary" 
              danger 
              size="small"
              @click="handleBatchDelete(record.name)"
            >
              批量删除 ({{ rowSelectionMap[record.name]?.selectedRowKeys?.length || 0 }})
            </a-button>
          </div>
          
          <x-table
            :columns="detailColumns"
            :data-source="record.detail"
            :pagination="false"
            :bordered="true"
            size="small"
            rowKey="id"
            :rowSelection="getRowSelection(record.name).value"
            @select="handleSelect"
            @selectAll="handleSelectAll"
          >
            <template #action="{ record }">
              <a @click="editConfig(record)" style="margin-right: 8px">编辑</a>
              <a @click="deleteConfig(record)">删除</a>
            </template>
          </x-table>
        </div>
        <div v-else style="padding: 16px; text-align: center; color: #999">
          暂无详情数据
        </div>
      </template>
    </x-table>
    <a-pagination
      :current="pageNo"
      :page-size="pageSize"
      :total="total"
      @change="onPageChange"
      style="margin-top: 16px; text-align: right"
    />

    <!-- 添加广告配置弹窗 -->
    <x-dialog
      :visible="addDialogVisible"
      title="添加广告配置"
      xWidth="400px"
      @cancel="closeAddDialog"
      @confirm="handleAddConfig"
      :confirmLoading="addLoading"
    >
      <a-form
        layout="vertical"
        :model="addForm"
        :rules="addFormRules"
        ref="addFormRef"
      >
        <a-form-item label="组名" name="name" required>
          <a-input v-model:value="addForm.name" placeholder="请输入组名" />
        </a-form-item>
        <a-form-item label="最大支持数" name="count" required>
          <a-input-number
            v-model:value="addForm.count"
            placeholder="请输入最大支持数"
            :min="1"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="广告地址" name="url" required>
          <a-select
            v-model:value="addForm.url"
            placeholder="请选择广告地址"
            style="width: 100%"
            @popupScroll="onPopupScroll"
            mode="multiple"
          >
            <a-select-option
              v-for="item in adsSourceOptions"
              :key="item.id"
              :value="item.url"
            >
              {{ item.url }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </x-dialog>

    <!-- 编辑广告配置弹窗 -->
    <x-dialog
      :visible="editDialogVisible"
      title="编辑广告配置"
      xWidth="400px"
      @cancel="closeEditDialog"
      @confirm="handleEditConfig"
      :confirmLoading="editLoading"
    >
      <a-form
        layout="vertical"
        :model="editForm"
        :rules="editFormRules"
        ref="editFormRef"
      >
        <a-form-item label="最大支持数" name="count" required>
          <a-input-number
            v-model:value="editForm.count"
            placeholder="请输入最大支持数"
            :min="1"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="广告地址" name="url" required>
          <a-input v-model:value="editForm.url" placeholder="请输入广告地址" />
        </a-form-item>
      </a-form>
    </x-dialog>

    <!-- 选择广告源弹窗 -->
    <x-dialog
      :visible="adsSourceDialogVisible"
      title="选择广告源"
      xWidth="600px"
      @cancel="closeAdsSourceDialog"
      @confirm="handleSelectAdsSource"
    >
      <x-table
        :columns="adsSourceColumns"
        :data-source="adsSourceOptions"
        :loading="adsSourceLoading"
        rowKey="id"
        :pagination="false"
        :customRow="customRow"
        :rowClassName="getRowClassName"
      />
      <a-pagination
        :current="adsSourcePage"
        :page-size="10"
        :total="adsSourceTotal"
        @change="onAdsSourcePageChange"
        style="margin-top: 16px; text-align: right"
        size="small"
      />
    </x-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, defineComponent, h, computed } from "vue";
import { message, Modal } from "ant-design-vue";
import XTable from "@/components/XATable.vue";
import XDialog from "@/components/XDialog.vue";
import {
  adsConfigList,
  adsConfigAdd,
  adsConfigDel,
  adsConfigDetailList,
  adsSourceList,
  adsConfigSaveGroupList,
} from "@/api/api";
import type { FormInstance } from "ant-design-vue";

const tableData = ref([]);
const tableDataLog = ref<any[]>([]);
const loading = ref(false);
const pageNo = ref(1);
const pageSize = ref(10);
const total = ref(0);
const addDialogVisible = ref(false);
const addForm = ref({
  name: "",
  count: 8,
  url: [] as string[],
});
const addFormRef = ref<FormInstance>();
const addLoading = ref(false);
const addFormRules = {
  name: [{ required: true, message: "请输入组名" }],
  count: [{ required: true, message: "请输入最大支持数" }],
  url: [{ required: true, message: "请输入广告地址" }],
};
const detailColumns = ref([
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
    width: 80,
  },
  {
    title: "组名",
    dataIndex: "name",
    key: "name",
  },
  {
    title: "最大支持数",
    dataIndex: "count",
    key: "count",
    width: 120,
  },
  {
    title: "广告地址",
    dataIndex: "url",
    key: "url",
  },
  //   {
  //     title: "创建时间",
  //     dataIndex: "createdAt",
  //     key: "createdAt",
  //     width: 180,
  //   },
  {
    title: "操作",
    dataIndex: "action",
    key: "action",
    width: 100,
  },
]);
const columns = ref([
  //   {
  //     title: "ID",
  //     dataIndex: "id",
  //     key: "id",
  //     width: 80,
  //   },
  {
    title: "组名",
    dataIndex: "name",
    key: "name",
  },
  //   {
  //     title: "最大支持数",
  //     dataIndex: "count",
  //     key: "count",
  //     width: 120,
  //   },
  //   {
  //     title: "广告地址",
  //     dataIndex: "url",
  //     key: "url",
  //   },
  //   {
  //     title: "操作",
  //     dataIndex: "action",
  //     key: "action",
  //     width: 100,
  //   },
]);

const expandableConfig = {
  expandedRowRender: (record: any) => {
    return h(DetailTable, { configId: record.id });
  },
};

const adsSourceOptions = ref<any[]>([]);
const adsSourceLoading = ref(false);
const adsSourcePage = ref(1);
const adsSourceHasMore = ref(true);
const adsSourceTotal = ref(0);

const loadAdsSourceOptions = async (reset = false) => {
  if (adsSourceLoading.value) return;

  adsSourceLoading.value = true;

  if (reset) {
    adsSourcePage.value = 1;
  }

  try {
    const res = await adsSourceList({
      pageNo: adsSourcePage.value,
      pageSize: 10,
    });

    if (res && res.data) {
      adsSourceOptions.value = res.data.records || [];
      adsSourceTotal.value = res.data.total || 0;
    } else {
      adsSourceOptions.value = [];
      adsSourceTotal.value = 0;
    }
  } catch (error) {
    console.error("获取广告源列表失败");
    adsSourceOptions.value = [];
    adsSourceTotal.value = 0;
  } finally {
    adsSourceLoading.value = false;
  }
};

const openAddDialog = async () => {
  addDialogVisible.value = true;
  addForm.value = { name: "", count: 8, url: [] };
  addFormRef.value?.resetFields();

  // 重置广告源数据和分页状态
  adsSourceOptions.value = [];
  adsSourcePage.value = 1;
  adsSourceTotal.value = 0;

  // 加载第一页广告源数据
  await loadAdsSourceOptions(true);
};

const closeAddDialog = () => {
  addDialogVisible.value = false;
};

const handleAddConfig = async () => {
  if (!addFormRef.value) return;
  try {
    await addFormRef.value.validate();
    addLoading.value = true;
    var params = {
      name: addForm.value.name,
      count: addForm.value.count,
      url: "",
    };
    var tempParamsArray: any[] = [];
    addForm.value.url.forEach((element: any) => {
      tempParamsArray.push({
        name: params.name,
        count: params.count,
        url: element,
      });
    });
    const res = await adsConfigSaveGroupList(tempParamsArray);
    if (res.code === "200") {
      message.success("添加成功");
      closeAddDialog();

      // 更新 tableDataLog 中相同组名的 detail 数据
      const targetLogItem = tableDataLog.value.find(
        (logItem) => logItem.name === addForm.value.name
      );
      if (targetLogItem) {
        // 重新获取该组的详情数据
        adsConfigDetailList({ name: addForm.value.name }).then((detailRes) => {
          targetLogItem.detail = detailRes.data;
          getList();
        });
      } else {
        getList();
      }
    } else {
      message.error(res.msg);
    }
  } catch (e) {
    // 校验失败自动提示
  } finally {
    addLoading.value = false;
  }
};

const deleteConfig = (record: any) => {
  Modal.confirm({
    title: "确认删除该广告配置？",
    content: `标题：${record.name}`,
    okText: "确定",
    cancelText: "取消",
    onOk: async () => {
      const params = { id: record.id };
      const res = await adsConfigDel(params);
      if (res.code === "200") {
        message.success("删除成功");

        // 更新 tableDataLog 中所有记录的 detail 数据，删除对应的详情项
        tableDataLog.value.forEach((logItem) => {
          if (logItem.detail && Array.isArray(logItem.detail)) {
            const detailIndex = logItem.detail.findIndex(
              (detailItem: any) => detailItem.id === record.id
            );
            if (detailIndex !== -1) {
              logItem.detail.splice(detailIndex, 1);
            }
          }
        });

        getList();
      } else {
        message.error(res.msg);
      }
    },
  });
};

// 为每一行单独记录选中状态
const rowSelectionMap = ref<Record<string, { selectedRowKeys: string[], selectedRows: any[] }>>({});
const currentExpandedRow = ref<string | null>(null);

// 获取指定行的选中状态
const getRowSelection = (rowName: string) => {
  return computed(() => ({
    selectedRowKeys: rowSelectionMap.value[rowName]?.selectedRowKeys || [],
    onChange: (keys: string[], rows: any[]) => {
      //console.log(`行 ${rowName} onChange 触发:`, keys, rows);
      if (!rowSelectionMap.value[rowName]) {
        rowSelectionMap.value[rowName] = { selectedRowKeys: [], selectedRows: [] };
      }
      rowSelectionMap.value[rowName].selectedRowKeys = keys;
      rowSelectionMap.value[rowName].selectedRows = rows;
    },
    type: 'checkbox',
    getCheckboxProps: (record: any) => ({
      disabled: record.disabled || false,
    }),
  }));
};

// 处理单行选择
const handleSelect = ({ record, selected, selectedRows }: any) => {
  //console.log('选中行:', record)
  //console.log('是否选中:', selected)
  //console.log('所有选中行:', selectedRows)
}

// 处理全选
const handleSelectAll = ({ selected, selectedRows, changeRows }: any) => {
  //console.log('是否全选:', selected)
  //console.log('所有选中行:', selectedRows)
  //console.log('变化行:', changeRows)
}

// 批量删除处理
const handleBatchDelete = (rowName: string) => {
  const selectedKeys = rowSelectionMap.value[rowName]?.selectedRowKeys || [];
  if (selectedKeys.length === 0) {
    message.warning('请先选择要删除的数据');
    return;
  }

  Modal.confirm({
    title: '确认批量删除？',
    content: `确定要删除选中的 ${selectedKeys.length} 条广告配置吗？`,
    okText: '确定',
    cancelText: '取消',
    onOk: async () => {
      await batchDeleteConfigs(rowName);
    },
  });
};

// 批量删除配置
const batchDeleteConfigs = async (rowName: string) => {
  const selectedKeys = rowSelectionMap.value[rowName]?.selectedRowKeys || [];
  const deletePromises = selectedKeys.map(async (id: string) => {
    try {
      const res = await adsConfigDel({ id });
      if (res.code === "200") {
        return { success: true, id };
      } else {
        return { success: false, id, error: res.msg };
      }
    } catch (error: any) {
      return { success: false, id, error: error.message || '删除失败' };
    }
  });

  try {
    const results = await Promise.all(deletePromises);
    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    if (successCount > 0) {
      message.success(`成功删除 ${successCount} 条配置`);
    }

    if (failCount > 0) {
      message.error(`删除失败 ${failCount} 条配置`);
    }

    // 保存要删除的ID列表
    const deletedIds = [...selectedKeys];

    // 清空当前行的选中状态
    if (rowSelectionMap.value[rowName]) {
      rowSelectionMap.value[rowName].selectedRowKeys = [];
      rowSelectionMap.value[rowName].selectedRows = [];
    }

    // 更新 tableDataLog 中对应记录的 detail 数据，删除对应的详情项
    const logItem = tableDataLog.value.find((item: any) => item.name === rowName);
    if (logItem && logItem.detail && Array.isArray(logItem.detail)) {
      deletedIds.forEach((deletedId) => {
        const detailIndex = logItem.detail.findIndex(
          (detailItem: any) => detailItem.id === deletedId
        );
        if (detailIndex !== -1) {
          logItem.detail.splice(detailIndex, 1);
        }
      });
    }

    // 刷新列表
    getList();
  } catch (error) {
    console.error('批量删除失败:', error);
    message.error('批量删除失败，请重试');
  }
};
const viewChannels = (record: any) => {
  message.info(`查看配置：${record.name}`);
};

const onPageChange = (page: number) => {
  pageNo.value = page;
  getList();
};

const getList = () => {
  loading.value = true;
  adsConfigList({
    // pageNo: pageNo.value,
    // pageSize: pageSize.value,
  })
    .then((res) => {
      tableData.value = res.data || [];
      total.value = res.data.total || 0;

      // 从 tableDataLog 中恢复之前展开的 detail 数据
      tableData.value.forEach((item: any) => {
        const logItem = tableDataLog.value.find(
          (log: any) => log.name === item.name
        );
        if (logItem && logItem.detail) {
          item.detail = logItem.detail;
        }
      });
    })
    .catch((e) => {
      message.error("获取广告配置列表失败");
    })
    .finally(() => {
      loading.value = false;
    });
};

const onExpand = ({ expanded, record }: any) => {
  if (expanded) {
    // 设置当前展开的行
    currentExpandedRow.value = record.name;
    
    // 立即保存 record 的引用，避免异步操作中被覆盖
    const currentRecord = { ...record };
    //console.log(currentRecord.name, "展开");

    // 展开时获取详情数据并记录到 tableDataLog
    adsConfigDetailList({ name: currentRecord.name })
      .then((res) => {
        // 使用保存的 currentRecord 引用
        currentRecord.detail = res.data;
        record.detail = res.data; // 同时更新原始 record
        //console.log("record", currentRecord, tableDataLog.value);

        // 记录到 tableDataLog 中
        const existingIndex = tableDataLog.value.findIndex(
          (item: any) => item.name === currentRecord.name
        );

        if (existingIndex !== -1) {
          tableDataLog.value[existingIndex] = currentRecord;
        } else {
          tableDataLog.value.push(currentRecord);
        }
      })
      .catch((error) => {
        console.error("获取详情数据失败:", error);
      });
  } else {
    // 关闭时清除当前展开行
    if (record && record.name === currentExpandedRow.value) {
      currentExpandedRow.value = null;
    }
    
    // 删除 tableDataLog 中的记录
    if (record && record.name) {
      const index = tableDataLog.value.findIndex(
        (item) => item.name === record.name
      );
      if (index !== -1) {
        tableDataLog.value.splice(index, 1);
      }
    }
  }
};
// 详情表格组件
const DetailTable = defineComponent({
  props: {
    configId: {
      type: [String, Number],
      required: true,
    },
  },
  setup(props) {
    const detailData = ref([]);
    const detailLoading = ref(false);

    const detailColumns = [
      { title: "ID", dataIndex: "id", key: "id", width: 80 },
      { title: "广告地址", dataIndex: "url", key: "url" },
      {
        title: "创建时间",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 180,
      },
    ];

    const getDetailList = () => {
      detailLoading.value = true;
      adsConfigDetailList({ configId: props.configId })
        .then((res) => {
          detailData.value = res.data.records || [];
        })
        .catch((e) => {
          message.error("获取详情列表失败");
        })
        .finally(() => {
          detailLoading.value = false;
        });
    };

    onMounted(() => {
      getDetailList();
    });

    return () =>
      h(XTable, {
        columns: detailColumns,
        dataSource: detailData.value,
        loading: detailLoading.value,
        rowKey: "id",
        bordered: true,
        pagination: false,
      });
  },
});

const editDialogVisible = ref(false);
const editForm = ref({
  id: 0,
  count: 0,
  url: "",
});
const editFormRef = ref<FormInstance>();
const editLoading = ref(false);
const editFormRules = {
  count: [{ required: true, message: "请输入最大支持数" }],
  url: [{ required: true, message: "请输入广告地址" }],
};

const editConfig = (record: any) => {
  editForm.value = {
    id: record.id,
    count: record.count,
    url: record.url,
  };
  editFormRef.value?.resetFields();
  editDialogVisible.value = true;
};

const closeEditDialog = () => {
  editDialogVisible.value = false;
};

const handleEditConfig = async () => {
  if (!editFormRef.value) return;
  try {
    await editFormRef.value.validate();
    editLoading.value = true;
    const params = {
      id: editForm.value.id,
      count: editForm.value.count,
      url: editForm.value.url,
    };
    const res = await adsConfigAdd(params);
    if (res.code === "200") {
      message.success("编辑成功");
      closeEditDialog();

      // 更新 tableDataLog 中对应记录的 detail 数据
      tableDataLog.value.forEach((logItem) => {
        if (logItem.detail && Array.isArray(logItem.detail)) {
          const detailIndex = logItem.detail.findIndex(
            (detailItem: any) => detailItem.id === editForm.value.id
          );
          if (detailIndex !== -1) {
            logItem.detail[detailIndex] = {
              ...logItem.detail[detailIndex],
              ...params,
            };
          }
        }
      });

      getList();
    } else {
      message.error(res.msg);
    }
  } catch (e) {
    // 校验失败自动提示
  } finally {
    editLoading.value = false;
  }
};

const adsSourceDialogVisible = ref(false);
const selectedAdsSourceKeys = ref<string[]>([]);
const selectedAdsSource = ref<string[]>([]);

const adsSourceColumns = ref([
  //   {
  //     title: '归属',
  //     dataIndex: 'remark',
  //     key: 'remark',
  //     width: 100,
  //   },
  {
    title: "广告地址",
    dataIndex: "url",
    key: "url",
  },
]);

const openAdsSourceDialog = async () => {
  adsSourceDialogVisible.value = true;
  selectedAdsSourceKeys.value = [];
  selectedAdsSource.value = [];

  // 重置并加载第一页广告源数据
  await loadAdsSourceOptions(true);
};

const closeAdsSourceDialog = () => {
  adsSourceDialogVisible.value = false;
};

const handleSelectAdsSource = () => {
  if (selectedAdsSource.value) {
    addForm.value.url.push(...selectedAdsSource.value);
    closeAdsSourceDialog();
  }
};

const customRow = (record: any) => {
  return {
    onClick: () => {
      selectedAdsSourceKeys.value = [record.id];
      selectedAdsSource.value = record;
      //console.log("选中行:", record);
    },
  };
};

const getRowClassName = (record: any) => {
  return selectedAdsSourceKeys.value.includes(record.id)
    ? "ant-table-row-selected"
    : "";
};

const onAdsSourcePageChange = (page: number) => {
  adsSourcePage.value = page;
  loadAdsSourceOptions();
};

const onPopupScroll = (e: any) => {
  //console.log("滚动", e);
  const { target } = e;
  //console.log("target", target, adsSourceOptions.value);
  // 判断是否滚动到底部
  if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
    //console.log("滚动到底部了");
    // 如果还有更多数据且当前没有在加载中，则加载下一页

    if (!adsSourceLoading.value) {
      adsSourcePage.value++;
      loadMoreAdsSourceOptions();
    }
  }
};

const loadMoreAdsSourceOptions = async () => {
  if (adsSourceLoading.value) return;
  adsSourceLoading.value = true;
  try {
    const res = await adsSourceList({
      pageNo: adsSourcePage.value,
      pageSize: 10,
    });
    if (res && res.data) {
      const newRecords = res.data.records || [];
      adsSourceOptions.value = [...adsSourceOptions.value, ...newRecords];
      adsSourceTotal.value = res.data.total || 0;
    }
  } catch (error) {
    console.error("加载更多广告源失败");
  } finally {
    adsSourceLoading.value = false;
  }
};

onMounted(() => {
  getList();
});
</script>

<style scoped>
:deep(.ant-table-row-selected) {
  background-color: #e6f7ff !important;
}

:deep(.ant-table-tbody > tr:hover) {
  cursor: pointer;
}

:deep(.ant-table-tbody > tr.ant-table-row-selected:hover) {
  background-color: #bae7ff !important;
}
</style>
